import React, { useEffect, useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
  Avatar,
  alpha,
  useTheme,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useNavigate } from 'react-router-dom';
import proxy from '../api/axios';
import useCachedQuery from '../hooks/useCachedQuery';
import {
  fetchNotifications,
  loadNotifications,
  queryKeys,
  STALE_TIMES,
  setQueryData,
} from '../api/queries';
import { timeAgo } from './problems/discussion/discussionUtils';
import { resolveProfileImage } from '../common/utils';

const ACCENT = '#137274';

const DiscussionNotificationBell = ({ isLoggedIn }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  const { data, loading } = useCachedQuery(
    queryKeys.notifications,
    fetchNotifications,
    { staleTime: STALE_TIMES.notifications, enabled: isLoggedIn },
  );

  const items = data?.items || [];
  const unreadCount = data?.unreadCount ?? 0;

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const id = setInterval(() => {
      loadNotifications().catch(() => {});
    }, 60_000);
    return () => clearInterval(id);
  }, [isLoggedIn]);

  const open = Boolean(anchor);

  const handleOpen = (e) => {
    setAnchor(e.currentTarget);
    loadNotifications().catch(() => {});
  };

  const handleClose = () => setAnchor(null);

  const goToDiscussion = async (n) => {
    handleClose();
    if (!n.read) {
      try {
        await proxy.patch(`/notifications/${n.id}/read`);
        setQueryData(queryKeys.notifications, {
          items: items.map((item) => (item.id === n.id ? { ...item, read: true } : item)),
          unreadCount: Math.max(0, unreadCount - 1),
        });
      } catch {
        /* ignore */
      }
    }
    if (n.problemSlug) {
      navigate(`/problems/${n.problemSlug}?discussion=${n.postId}`);
    }
  };

  const markAllRead = async () => {
    try {
      await proxy.patch('/notifications/read-all');
      setQueryData(queryKeys.notifications, {
        items: items.map((n) => ({ ...n, read: true })),
        unreadCount: 0,
      });
    } catch {
      /* ignore */
    }
  };

  if (!isLoggedIn) return null;

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label="Discussion notifications">
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 340,
            maxWidth: '92vw',
            maxHeight: 420,
            mt: 1,
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>Discussions</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllRead} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {loading && !items.length && (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={22} sx={{ color: ACCENT }} />
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
            <ForumOutlinedIcon sx={{ color: isDark ? '#8b949e' : '#5f6b73', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        )}

        {items.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => goToDiscussion(n)}
              sx={{
                alignItems: 'flex-start',
                py: 1.25,
                bgcolor: n.read ? 'transparent' : alpha(ACCENT, isDark ? 0.12 : 0.06),
                whiteSpace: 'normal',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', width: '100%' }}>
                <Avatar
                  src={resolveProfileImage(n.actor?.profileImage)}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    bgcolor: alpha(ACCENT, 0.18),
                    color: ACCENT,
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                >
                  {(n.actor?.name || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: n.read ? 500 : 700, lineHeight: 1.4 }}>
                    {n.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {timeAgo(n.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default DiscussionNotificationBell;
